interface HeroSectionProps {
  backgroundImage: string;
  eyebrow?: string;
  headline: string;
  headlineAccent?: string;
  subheadline: string;
  ctaText: string;
  ctaHref: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
}

const HeroSection = ({
  backgroundImage,
  eyebrow,
  headline,
  headlineAccent,
  subheadline,
  ctaText,
  ctaHref,
  secondaryCtaText,
  secondaryCtaHref,
}: HeroSectionProps) => (
  <section className="relative h-screen min-h-[740px] flex items-center overflow-hidden">
    {/* Background image */}
    <div className="absolute inset-0">
      <img
        src={backgroundImage}
        alt="Polished Concrete Plus — Premium Concrete Finishing Minnesota"
        className="w-full h-full object-cover object-top"
      />
      {/* Multi-layer gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
      {/* Radial vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 100% at 20% 50%, transparent 30%, rgba(0,0,0,0.55) 100%)',
        }}
      />
      {/* Subtle warm tint on right */}
      <div className="absolute inset-0 bg-gradient-to-l from-[#c8a951]/8 via-transparent to-transparent" />
    </div>

    {/* Left gold accent bar */}
    <div className="absolute left-0 top-0 bottom-0 w-[3px]">
      <div className="h-full bg-gradient-to-b from-transparent via-[#c8a951]/50 to-transparent" />
    </div>

    {/* Content */}
    <div className="relative z-10 max-w-screen-xl mx-auto px-8 lg:px-14 w-full pt-28">
      <div className="max-w-[660px]">
        {eyebrow && (
          <div className="animate-fade-in animate-delay-1 flex items-center gap-3 mb-7">
            <div className="w-10 h-px bg-[#c8a951]" />
            <p className="text-[#c8a951] text-[10px] uppercase tracking-[0.45em] font-bold">
              {eyebrow}
            </p>
          </div>
        )}
        <h1
          className="animate-fade-up animate-delay-1 font-bold text-white leading-[1.04] mb-8"
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(44px, 6vw, 78px)',
          }}
        >
          {headline}
          {headlineAccent && (
            <>
              <br />
              <em className="text-[#c8a951] not-italic">{headlineAccent}</em>
            </>
          )}
        </h1>
        <p className="animate-fade-up animate-delay-2 text-white/60 text-base lg:text-lg leading-relaxed mb-10 max-w-[520px]">
          {subheadline}
        </p>
        <div className="animate-fade-up animate-delay-3 flex flex-wrap gap-4 items-center">
          <a
            href={ctaHref}
            className="bg-[#c8a951] text-[#0a0a0a] text-xs font-bold px-10 py-4 uppercase tracking-[0.22em] hover:bg-[#e0c070] transition-colors whitespace-nowrap cursor-pointer"
          >
            {ctaText}
          </a>
          {secondaryCtaText && (
            <a
              href={secondaryCtaHref ?? '#'}
              className="text-white border border-white/30 text-xs font-medium px-10 py-4 uppercase tracking-[0.22em] hover:border-[#c8a951] hover:text-[#c8a951] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {secondaryCtaText}
            </a>
          )}
        </div>
      </div>
    </div>

    {/* Bottom floating info strip */}
    <div className="absolute bottom-0 left-0 right-0">
      <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent h-28" />
      <div className="absolute bottom-0 left-0 right-0 px-8 lg:px-14 pb-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="animate-fade-in animate-delay-4 flex items-center gap-4">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-phone-fill text-[#c8a951] text-sm" />
          </div>
          <div>
            <p className="text-white/30 text-[9px] uppercase tracking-[0.35em]">Free Estimate</p>
            <a href="tel:3204931454" className="text-white text-sm font-semibold hover:text-[#c8a951] transition-colors">
              320.493.1454
            </a>
          </div>
        </div>
        <div className="hidden lg:flex items-center gap-6">
          <div className="flex items-center gap-2 text-white/30 text-xs">
            <i className="ri-map-pin-2-fill text-[#c8a951]" />
            Sauk Rapids, MN — Serving 5 States
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-px h-8 bg-gradient-to-b from-[#c8a951]/50 to-transparent" />
            <p className="text-white/25 text-[9px] uppercase tracking-[0.4em]">Scroll</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HeroSection;
